import React, { useState, useEffect, useCallback } from "react";
import CustomTable from "src/components/App/AppGrid/CustomTable/CustomTable";
import { Avatar, Chip, Box, alpha } from "@mui/material";
import theme from "src/config/theme";
import ButtonComponent from "src/components/shared/Button";
import DialogBox from "src/components/shared/Dialog/DialogBox";
import { ReactComponent as Edit } from '../../../../../assets/images/edit-2.svg';
import { ReactComponent as Delete } from '../../../../../assets/images/trash.svg';
import { ReactComponent as Add } from '../../../../../assets/images/add-square.svg';
import useResponsive from "src/components/hooks/useResponsive";
import TreatmentTabForm from "../Forms/TreatmentTabForm";
import TypeSitting from "../Forms/TypeSitting";
import TypeVisiting from "../Forms/TypeVisiting";
import NoDataFound from "../../NoDataFound/NoDataFound";
import { useDispatch, useSelector } from "react-redux";
import { getTreatmentsByPatientIdRequest } from "src/modules/app/store/appActions";
import dayjs from "dayjs";
import useAppointmentDetails from "../../UseAppointmentDetails";
import { AllTabsMainContainer } from "../Style";
import TreatmentCard from "../CardView/TreatmentCard";

const columns = [
  { id: "treatmentName", label: "Treatment Name", sx: { width: "486px", minWidth: "150px", textAlign: 'left' } },
  { id: "type", label: "Type", sx: { textAlign: 'left' }, },
  { id: "schduleDays", label: "Schedule Days", sx: { "& .css-axw7ok": { justifyContent: 'center' } } },
  { id: 'dateOrTime', label: 'Date Or Time', sx: { "& .css-axw7ok": { justifyContent: 'center' } } },
  { id: 'status', label: 'Status', sx: { "& .css-axw7ok": { justifyContent: 'center' } } },
];


const Treatments = ({ shouldRefresh }) => {
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const [open, setOpen] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const dispatch = useDispatch();
  const [treatment, setTreatment] = useState([]);
  const { PatientId } = useAppointmentDetails();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState();
  const updatedTreatment = (newTreat) => {
    setTreatment((prevTreat) => [newTreat, ...prevTreat]);
  }

  const formatDate = (timestamp) => {
    return dayjs(timestamp).format("h:mmA");
  };
  const fetchTreatments = useCallback(
    (page = 0, perPage = 10) => {
      const fetchData = async () => {
        const params = {
          page: page + 1,
          limit: perPage,
        };

        const response = await new Promise((resolve, reject) => {
          dispatch(getTreatmentsByPatientIdRequest({ patientId: PatientId, params }, resolve, reject));
        });

        if (response?.data && response?.meta) {
          setTreatment(response.data);
          setTotalCount(response.meta.totalRecords);

        } else {
          console.log('Error fetching Treatments');
        }

      }
      fetchData();

    },
    [dispatch, PatientId],
  );
  useEffect(() => {
    if (PatientId) {
      fetchTreatments(page, perPage);
    }
  }, [PatientId, fetchTreatments, page, perPage, shouldRefresh]);

  const handleOpen = (treatment = null) => {
    setSelectedTreatment(treatment);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setSelectedTreatment(null);
  };
  const handleExited = () => {
    setSelectedTreatment(null);
  };
  const getDialogTitle = () => {
    if (!open) return null;
    if (selectedTreatment) {
      return `${selectedTreatment?.treatmentName} Treatments`;
    }
    return `Add Treatment`;
  }
  const getDialogContent = () => {
    if (!open) return null;

    const commonProps = {
      onClose: handleClose,
      treatmentName: selectedTreatment?.treatmentName,
      treatmentId: selectedTreatment?.treatmentId,
    };

    if (selectedTreatment?.type === "visiting" || selectedTreatment?.type === "visit") {
      return <TypeVisiting {...commonProps} />;
    }

    if (selectedTreatment?.type === "sitting") {
      return <TypeSitting {...commonProps} fetchTreatments={fetchTreatments} />;
    }

    return (
      <TreatmentTabForm
        onClose={handleClose}
        updatedTreatment={updatedTreatment}
        fetchTreatments={fetchTreatments}
      />
    );
  };
  const dayMap = {
    M: 'Mon',
    T: 'Tue',
    W: 'Wed',
    Th: 'Thu',
    F: 'Fri',
    S: 'Sat',
    Su: 'Sun'
  };
  const data = treatment.map((treatmentData) => ({
    treatmentId: treatmentData.id,
    treatmentName: treatmentData.name,
    type: treatmentData.treatmentType,
    schduleDays: treatmentData.treatmentType === 'visiting'
      ? treatmentData.days?.map(day => dayMap[day.toLowerCase()] || day).join(', ')
      : '-',
    dateOrTime: treatmentData.treatmentType === 'visiting'
      ? (treatmentData.time || '-')
      : treatmentData.dateTimes && treatmentData.dateTimes.length > 0
        ? `${dayjs(treatmentData.dateTimes[0].date).format('DD-MM-YYYY')} , ${treatmentData.dateTimes[0].time}`
        : '-',
    status: `${treatmentData.remaining}/${treatmentData.total} Complete`,
  }))

  const actions = [
    { label: "Edit", icon: <Edit />, onClick: (row) => alert(`Viewing ${row.patient}`) },
    { label: "Delete", icon: <Delete />, onClick: (row) => alert(`Attending ${row.patient}`) },
  ];
  return (
    <>
      <AllTabsMainContainer>
        {(isMobile || isTablet) ?
          <TreatmentCard
            treatment={treatment}
            page={page}
            perPage={perPage}
            setPage={setPage}
            totalCount={totalCount}
            fetchTreatments={fetchTreatments}
          />
          :
          <CustomTable
            disabled={true}
            key={page}
            columns={columns}
            data={data.map((row) => ({
              ...row,
              status: (
                <Chip
                  label={row.status}
                  onClick={() => handleOpen(row)}
                  sx={{
                    background: 'rgba(103, 57, 150, 0.14)',
                    color: theme.palette.action.purpleLight,
                    cursor: 'pointer',
                    ...theme.typography.small,
                    borderRadius: '100px',
                    padding: '10px 18px',
                  }}
                />
              ),
            }))}
            actions={actions}
            page={page}
            onPageChange={(newPage) => {
              setPage(newPage);
            }}
            onPerPageChange={(newPerPage) => {
              setPerPage(newPerPage);
              setPage(0);
            }}
            perPage={perPage}
            totalCount={totalCount}
            noDataComponent={
              <NoDataFound
                title="No Treatments Found!"
                showButton={false}
                description="No treatment plans assigned to this patient yet."
              />
            }
          />
        }

        <DialogBox open={open} onClose={handleClose} onExited={handleExited} title={getDialogTitle()} >
          {getDialogContent()}
        </DialogBox>
      </AllTabsMainContainer>
    </>
  )
};

export default Treatments;

